import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import http from '../../http';
import dayjs from 'dayjs';
import { reportClientMonitorEvent } from '../../clientMonitor';
import { formatMoney } from '../../money';

const C = {
    brand: '#F777B0',
    ink: '#1A1614',
    ink2: '#3D3632',
    ink3: '#8A8079',
    paper: '#EDE6D5',
    paper2: '#E2D9C6',
    hair: '#E8E0D0',
    hair2: '#D4CABA',
    serif: '"Instrument Serif", "Songti SC", serif',
    sans: '"Inter", "Noto Sans SC", -apple-system, sans-serif',
};

const PasswordVrifyDialog = observer(({ anonymousUserStore, open, pwd, orderId, customerUserId, onVerified }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [cover, setCover] = useState(null);
    const [customerName, setCustomerName] = useState(null);
    const [shootTime, setShootTime] = useState(null);
    const [totalPhotos, setTotalPhotos] = useState(null);
    const [photoCount, setPhotoCount] = useState(null);
    const [pricePerPic, setPricePerPic] = useState(null);
    const [hasPackage, setHasPackage] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const CODE_LENGTH = 4;
    const inputsRef = useRef([]);
    const verifyFnRef = useRef(null);
    const autoVerifiedPasswordRef = useRef(null);
    const verifyInFlightRef = useRef(false);

    const setDigitAt = useCallback((index, digit) => {
        const chars = Array.from({ length: CODE_LENGTH }, (_, i) => (password[i] || ''));
        chars[index] = digit;
        const next = chars.join('').replace(/\D/g, '').slice(0, CODE_LENGTH);
        setPassword(next);
        return next;
    }, [password]);

    const handleChange = useCallback((index) => (e) => {
        const raw = e.target.value || '';
        const v = raw.replace(/\D/g, '');
        let next = password;
        if (!v) {
            next = setDigitAt(index, '');
        } else if (v.length > 1) {
            const chars = Array.from({ length: CODE_LENGTH }, (_, i) => (password[i] || ''));
            v.slice(0, CODE_LENGTH - index).split('').forEach((digit, offset) => {
                chars[index + offset] = digit;
            });
            next = chars.join('').replace(/\D/g, '').slice(0, CODE_LENGTH);
            setPassword(next);
            inputsRef.current[Math.min(index + v.length, CODE_LENGTH - 1)]?.focus();
        } else {
            next = setDigitAt(index, v[0]);
            if (index < CODE_LENGTH - 1) {
                inputsRef.current[index + 1]?.focus();
                inputsRef.current[index + 1]?.select?.();
            }
        }
        setError(false);
        setErrorMessage(null);
        if (next.length === CODE_LENGTH && /^\d{4}$/.test(next)) {
            verifyFnRef.current?.(next);
        }
    }, [setDigitAt, password]);

    const handleKeyDown = useCallback((index) => (e) => {
        if (e.key === 'Backspace') {
            if (password[index]) {
                setDigitAt(index, '');
            } else if (index > 0) {
                inputsRef.current[index - 1]?.focus();
                setDigitAt(index - 1, '');
            }
            setError(false);
            setErrorMessage(null);
        } else if (e.key === 'ArrowLeft') {
            inputsRef.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight') {
            inputsRef.current[index + 1]?.focus();
        }
    }, [password, setDigitAt]);

    const handlePaste = useCallback((index) => (e) => {
        const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
        const digits = text.replace(/\D/g, '');
        if (!digits) return;
        e.preventDefault();
        let chars = Array.from({ length: CODE_LENGTH }, (_, i) => (password[i] || ''));
        let p = index;
        for (const d of digits) {
            if (p >= CODE_LENGTH) break;
            chars[p] = d;
            p++;
        }
        const next = chars.join('').replace(/\D/g, '').slice(0, CODE_LENGTH);
        setPassword(next);
        setError(false);
        setErrorMessage(null);
        inputsRef.current[Math.min(p, CODE_LENGTH - 1)]?.focus();
        if (next.length === CODE_LENGTH && /^\d{4}$/.test(next)) {
            verifyFnRef.current?.(next);
        }
    }, [password]);

    const getOrderInfo = useCallback(() => {
        http.get(`/customer/order/${orderId}/info`)
            .then(response => {
                const data = response.data;
                setCover(data.coverUrl);
                setCustomerName(data.customerName);
                setShootTime(data.shootTime);
                if (data.totalPhotos) setTotalPhotos(data.totalPhotos);
                if (data.photoCount) setPhotoCount(data.photoCount);
                if (data.pricePerPic) setPricePerPic(data.pricePerPic);
                setHasPackage(Boolean(data.hasPackage));
            })
            .catch(() => {});
    }, [orderId]);

    useEffect(() => {
        if (pwd && autoVerifiedPasswordRef.current !== pwd) {
            autoVerifiedPasswordRef.current = pwd;
            setPassword(pwd);
            verifyPasswordRequest(pwd);
        } else {
            getOrderInfo();
        }
    }, [getOrderInfo]);

    const verifyPasswordRequest = useCallback(async (pw) => {
        if (!/^\d{4}$/.test(pw) || verifyInFlightRef.current) return;
        verifyInFlightRef.current = true;
        setVerifying(true);
        setError(false);
        setErrorMessage(null);
        try {
            const response = await http.post(`/customer/order/${orderId}/verifyPassword`, { password: pw, customerUserId });
            reportClientMonitorEvent('selection_access_verified', {
                orderRef: orderId,
                accessMethod: pwd ? 'link_prefill' : 'manual',
                immediate: true,
            });
            anonymousUserStore.save(response.data.token);
            onVerified?.(pw);
        } catch (error) {
            if (error.response?.status === 410) {
                reportClientMonitorEvent('selection_access_failed', {
                    level: 'warn', orderRef: orderId, failureKind: 'expired_share',
                });
                setPassword('');
                setError(true);
                setErrorMessage('分享链接已过期，请联系摄影师');
            } else if (error.response?.status === 401) {
                reportClientMonitorEvent('selection_access_failed', {
                    level: 'warn', orderRef: orderId, failureKind: 'invalid_pin',
                });
                getOrderInfo();
                setPassword('');
                setError(true);
                setErrorMessage('提取码错误');
                setTimeout(() => inputsRef.current[0]?.focus(), 100);
            } else {
                reportClientMonitorEvent('selection_access_failed', {
                    level: 'warn', orderRef: orderId,
                    failureKind: 'network_error', httpStatus: error.response?.status || 0,
                });
                setError(true);
                setErrorMessage('暂时无法进入，请点击按钮重试');
            }
        } finally {
            verifyInFlightRef.current = false;
            setVerifying(false);
        }
    }, [orderId, customerUserId, onVerified, anonymousUserStore, getOrderInfo, pwd]);

    useEffect(() => { verifyFnRef.current = verifyPasswordRequest; }, [verifyPasswordRequest]);

    const handleSubmit = useCallback((event) => {
        event?.preventDefault();
        verifyPasswordRequest(password);
    }, [password, verifyPasswordRequest]);

    if (!open) return null;

    const dateStr = shootTime ? dayjs(shootTime).format('M 月 D 日') : '';

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            background: C.paper,
            display: 'flex', flexDirection: 'column',
            fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
            overflow: 'hidden',
        }}>
            {/* hero photo */}
            <div style={{
                height: '46%',
                flexShrink: 0,
                position: 'relative',
                background: cover
                    ? `url(${cover}) center/cover no-repeat`
                    : 'linear-gradient(155deg, #d8a87a, #5a3a2a)',
                overflow: 'hidden',
            }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'radial-gradient(ellipse at 30% 20%, rgba(255,230,200,.4), transparent 55%)',
                }} />
                {!cover && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        color: 'rgba(255,255,255,.18)', fontSize: 22, fontWeight: 600,
                        letterSpacing: 6, transform: 'rotate(-15deg)',
                        display: 'grid', placeItems: 'center',
                        fontFamily: C.serif,
                    }}>图派 · 图派 · 图派</div>
                )}
                {totalPhotos && (
                    <div style={{
                        position: 'absolute', bottom: 16, left: 18,
                        fontFamily: C.serif, fontSize: 12, color: 'rgba(255,255,255,.7)',
                        letterSpacing: 2,
                    }}>{totalPhotos} photographs · awaiting</div>
                )}
            </div>

            {/* card */}
            <div style={{
                flex: 1, padding: '24px 24px 16px',
                display: 'flex', flexDirection: 'column',
                overflowY: 'auto',
            }}>
                <div style={{ fontSize: 11, letterSpacing: 1.5, color: C.brand, fontWeight: 600, textTransform: 'uppercase' }}>
                    选片邀请
                </div>
                <h1 style={{
                    fontFamily: C.serif, fontWeight: 400,
                    fontSize: 30, lineHeight: 1.1, margin: '8px 0 6px',
                    color: C.ink, letterSpacing: -0.3,
                }}>{customerName || ' '}</h1>
                {dateStr && (
                    <div style={{ fontSize: 13, color: C.ink3 }}>{dateStr}</div>
                )}

                <div style={{ marginTop: 28, fontSize: 13, color: C.ink2, lineHeight: 1.6 }}>
                    请输入摄影师告知你的<b style={{ color: C.ink }}>4 位密码</b>，进入选片相册。
                </div>

                <form onSubmit={handleSubmit} style={{ margin: 0 }}>
                {/* PIN inputs */}
                <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
                    {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                        <input
                            key={i}
                            ref={el => (inputsRef.current[i] = el)}
                            type="tel"
                            inputMode="numeric"
                            enterKeyHint={i === CODE_LENGTH - 1 ? 'go' : 'next'}
                            autoComplete={i === 0 ? 'one-time-code' : 'off'}
                            maxLength={i === 0 ? CODE_LENGTH : 1}
                            value={password[i] || ''}
                            onChange={handleChange(i)}
                            onKeyDown={handleKeyDown(i)}
                            onPaste={handlePaste(i)}
                            onFocus={e => e.target.select()}
                            style={{
                                width: 56, height: 64,
                                fontFamily: C.serif, fontSize: 32, textAlign: 'center',
                                background: '#fff',
                                color: error ? '#c2412e' : C.ink,
                                border: `1.5px solid ${error ? '#e88c7c' : (password[i] ? C.ink : C.hair2)}`,
                                borderRadius: 12, outline: 'none',
                                transition: 'border-color .2s, color .2s',
                                animation: error ? 'cshake .35s' : 'none',
                            }}
                        />
                    ))}
                </div>
                {error && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#c2412e' }}>
                        {errorMessage || '提取码错误'}
                    </div>
                )}

                <button
                    type="submit"
                    data-testid="customer-pin-submit"
                    disabled={password.length !== CODE_LENGTH || verifying}
                    style={{
                        width: '100%', marginTop: 16, padding: '14px 16px', border: 'none',
                        borderRadius: 12, fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                        background: password.length === CODE_LENGTH && !verifying ? C.ink : C.hair2,
                        color: '#fff', cursor: password.length === CODE_LENGTH && !verifying ? 'pointer' : 'default',
                        transition: 'background .2s, opacity .2s',
                    }}
                >{verifying ? '正在进入…' : '进入相册'}</button>
                </form>

                <div style={{ flex: 1, minHeight: 24 }} />

                <div style={{
                    padding: '14px 16px', borderRadius: 10,
                    background: C.paper2, border: `1px solid ${C.hair}`,
                    fontSize: 12, color: C.ink3, lineHeight: 1.5,
                }}>
                    {photoCount
                        ? <>ⓘ {hasPackage ? '套餐含' : '本订单可选'} <b style={{ color: C.ink2 }}>{photoCount} 张精修</b>，加片 <b style={{ color: C.brand }}>¥{formatMoney(pricePerPic || 30)} / 张</b>。链接 14 天后失效。</>
                        : <>ⓘ 输入密码后进入相册，选择你最喜欢的照片。</>
                    }
                </div>
            </div>

            <style>{`
                @keyframes cshake {
                    0%,100%{transform:translateX(0)}
                    25%{transform:translateX(-6px)}
                    75%{transform:translateX(6px)}
                }
            `}</style>
        </div>
    );
});

export { PasswordVrifyDialog };
